// src/ui/screens/verification/CategorySelectionPage.jsx

import React from 'react';

const COLORS = {
  orange: '#FF8C08',
  bg: '#08090B',
  card: '#23262C',
  cardLight: '#2C2F36',
};

/** Category type constants used across verification & future gated features. */
export const SellerCategoryType = {
  personal: 'Personal',
  business: 'Business&Organizations',
};

export const PERSONAL_CATEGORIES = [
  'Property Owner',
  'Landlord',
  'Homeowner',
  'Property Investor',
  'Real Estate Agent',
  'Real Estate Broker',
  'Property Manager',
  'Real Estate Consultant',
  'Real Estate Developer',
  'Valuer/Surveyor',
  'Architect',
  'Interior Designer',
  'Contractor/Builder',
  'Legal Professional',
  'Mortgage Broker',
  'Property Photographer',
  'Supplier',
  'Engineer',
  'Property Blogger/Influencer',
  'Tour Guide',
  'Moving & Relocation Provider',
  'Other',
];

export const BUSINESS_CATEGORIES = [
  'Real Estate Agency',
  'Property Management Company',
  'Property Developer',
  'Manufacturing Company',
  'Supplier Company',
  'Construction Company',
  'Property Consultancy',
  'Mortgage Company',
  'Valuation Company',
  'Surveying Company',
  'Architecture Firm',
  'Interior Design Company',
  'Engineering Company',
  'Legal Firm',
  'Property Marketing Company',
  'Hotel',
  'Guest House',
  'Apartment Hotel',
  'Vacation Rental Company',
  'Commercial Property Owner',
  'Moving & Relocation Company',
  'Government Agency',
  'Non-Governmental Organization (NGO)',
  'Other',
];

// ── Icons ────────────────────────────────────────────────────────────────

const CloseIcon = () => (
  <svg className="w-[18px] h-[18px] text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
  </svg>
);

const PersonIcon = () => (
  <svg className="w-[19px] h-[19px]" style={{ color: COLORS.orange }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
  </svg>
);

const ApartmentIcon = () => (
  <svg className="w-[19px] h-[19px]" style={{ color: COLORS.orange }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 21h18M5 21V5a2 2 0 012-2h4a2 2 0 012 2v16M15 21v-9a2 2 0 012-2h2a2 2 0 012 2v9M9 7h.01M9 11h.01M9 15h.01" />
  </svg>
);

const ArrowIcon = ({ active }) => (
  <svg
    className="w-3.5 h-3.5"
    style={{ color: active ? COLORS.orange : 'rgba(255,255,255,0.24)' }}
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
  >
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M9 5l7 7-7 7" />
  </svg>
);

const RadioCheckedIcon = () => (
  <svg className="w-5 h-5" style={{ color: COLORS.orange }} fill="currentColor" viewBox="0 0 24 24">
    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 16a6 6 0 110-12 6 6 0 010 12z" />
    <circle cx="12" cy="12" r="4" />
  </svg>
);

// ── Section header ───────────────────────────────────────────────────────

const SectionHeader = ({ title, subtitle, icon }) => (
  <div className="flex items-center gap-3">
    <div
      className="w-9 h-9 rounded-[10px] flex items-center justify-center flex-shrink-0"
      style={{ backgroundColor: 'rgba(255,140,8,0.15)' }}
    >
      {icon}
    </div>
    <div className="flex-1 min-w-0">
      <p className="text-white text-base font-bold">{title}</p>
      <p className="text-white/40 text-xs mt-0.5">{subtitle}</p>
    </div>
  </div>
);

// ── Category tile ────────────────────────────────────────────────────────

const CategoryTile = ({ label, type, isSelected, onSelect }) => (
  <button
    type="button"
    onClick={() => onSelect(label, type)}
    className="w-full mb-2.5 px-4 py-3.5 rounded-[14px] flex items-center text-left transition-colors"
    style={{
      backgroundColor: COLORS.card,
      border: `${isSelected ? 1.5 : 1}px solid ${isSelected ? COLORS.orange : 'rgba(255,255,255,0.07)'}`,
    }}
  >
    <span
      className="flex-1 text-[14.5px]"
      style={{
        color: isSelected ? COLORS.orange : '#FFFFFF',
        fontWeight: isSelected ? 700 : 500,
      }}
    >
      {label}
    </span>
    {isSelected ? <RadioCheckedIcon /> : <ArrowIcon active={false} />}
  </button>
);

// ── CategorySelectionPage ────────────────────────────────────────────────

/**
 * Full-screen category picker, ported 1:1 from category_selection_page.dart.
 *
 * Props:
 *  - selectedCategory: string | null — currently selected category label
 *  - onSelect(category, categoryType): called when a tile is tapped
 *  - onClose(): called when the close button is tapped
 */
export const CategorySelectionPage = ({ selectedCategory, onSelect, onClose }) => {
  return (
    <div className="fixed inset-0 z-50 flex flex-col" style={{ backgroundColor: COLORS.bg }}>
      {/* Top bar */}
      <div className="flex items-center gap-3 px-3 pt-3 pb-1">
        <button
          type="button"
          onClick={onClose}
          className="w-10 h-10 rounded-full flex items-center justify-center border border-white/10 flex-shrink-0"
          style={{ backgroundColor: COLORS.card }}
        >
          <CloseIcon />
        </button>
        <h1 className="text-white text-xl font-bold">Select Category</h1>
      </div>

      {/* Scrollable list */}
      <div className="flex-1 overflow-y-auto px-4 pt-2 pb-8">
        <SectionHeader
          title="Personal"
          subtitle="Individuals verified with a National ID"
          icon={<PersonIcon />}
        />
        <div className="mt-3">
          {PERSONAL_CATEGORIES.map((label) => (
            <CategoryTile
              key={label}
              label={label}
              type={SellerCategoryType.personal}
              isSelected={selectedCategory === label}
              onSelect={onSelect}
            />
          ))}
        </div>

        <div className="h-7" />

        <SectionHeader
          title="Business & Organizations"
          subtitle="Verified with a Registration Document/Certificate"
          icon={<ApartmentIcon />}
        />
        <div className="mt-3">
          {BUSINESS_CATEGORIES.map((label) => (
            <CategoryTile
              key={label}
              label={label}
              type={SellerCategoryType.business}
              isSelected={selectedCategory === label}
              onSelect={onSelect}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

export default CategorySelectionPage;